import { IFlashcard } from '@sdk/contracts';
import { useEffect, useMemo } from 'react';
import { create } from 'zustand';

import { useMutateFlashcardPracticeTranscribeAnswer } from './useMutateFlashcardPracticeTranscribeAnswer';

interface IStore {
  flashcardList: IFlashcard[];
  currentFlashcardId: string | null;
  showFlashcardAnswerForId: string | null;
  showWriteAnswerForId: string | null;
  setFlashcardList: (list: IFlashcard[]) => void;
  setShowFlashcardAnswerForId: (id: string | null) => void;
  setShowWriteAnswerForId: (id: string | null) => void;
  setCurrentFlashcardId: (id: string) => void;
}

const store = create<IStore>(set => ({
  flashcardList: [],
  currentFlashcardId: null,
  showFlashcardAnswerForId: null,
  showWriteAnswerForId: null,
  setFlashcardList: list => set({ flashcardList: list }),
  setCurrentFlashcardId: (id: string) => set({ currentFlashcardId: id }),
  setShowFlashcardAnswerForId: (id: string | null) => set({ showFlashcardAnswerForId: id }),
  setShowWriteAnswerForId: (id: string | null) => set({ showWriteAnswerForId: id })
}));

export const useFlashcardNavigator = (flashcardList: IFlashcard[]) => {
  const {
    currentFlashcardId,
    setCurrentFlashcardId,
    showFlashcardAnswerForId,
    setShowFlashcardAnswerForId,
    showWriteAnswerForId,
    setShowWriteAnswerForId
  } = store();
  const { setTranscription, transcription } = useMutateFlashcardPracticeTranscribeAnswer();

  useEffect(() => {
    if (flashcardList.length > 0 && !currentFlashcardId) {
      setCurrentFlashcardId(flashcardList[0]._id);
    }
  }, [flashcardList, currentFlashcardId]);

  const canUseKeyboardNavigation = useMemo(() => {
    const isAnswerShown = showFlashcardAnswerForId !== null;

    return !(transcription.length > 0 && !isAnswerShown) || showWriteAnswerForId !== null;
  }, [transcription, showFlashcardAnswerForId, showWriteAnswerForId]);

  const currentFlashcard = useMemo(() => {
    return (
      flashcardList.find(flashcard => flashcard._id === currentFlashcardId) ?? flashcardList[0]
    );
  }, [flashcardList, currentFlashcardId]);

  const handleChangeFlashcard = () => {
    setShowFlashcardAnswerForId(null);
    setShowWriteAnswerForId(null);
    setTranscription('');
  };

  const canHideFlashcard = useMemo(() => {
    return flashcardList.length > 1;
  }, [flashcardList]);

  return {
    currentFlashcard,
    currentFlashcardId,
    handleChangeFlashcard,
    showFlashcardAnswerForId,
    setShowFlashcardAnswerForId,
    showWriteAnswerForId,
    setShowWriteAnswerForId,
    canHideFlashcard,
    setCurrentFlashcardId,
    canUseKeyboardNavigation
  };
};
